import { useEffect } from 'react'

import { Button } from '@/components/ui/button'
import { CopyToClipboard } from '@/components/ui/copy-to-clipboard'
import RenderIf from '@/components/ui/render-if'
import { Section } from '@/components/ui/section'
import { Separator } from '@/components/ui/separator'
import { Text } from '@/components/ui/text'
import { TooltipInfo } from '@/components/ui/tooltip-v2'
import { PATH_NAME } from '@/config'
import { EXTERNAL_LINKS } from '@/constants/external-link'
import { useMutationGenerateReferralCode } from '@/data'
import { useQueryAccountCommissionSummary, useQueryReferralStatistics } from '@/data/referral'
import { useIsMd } from '@/hooks'
import { CommonCopy, CommonGift } from '@/icons'
import { useReferralWallet } from '@/store'
import { formatNumber } from '@/utils'
import { APP_URL, DURATIONS } from '@/utils/constants'

import { Trans, useTranslation } from 'react-i18next'

import { ReferralInformationOverview } from './overview'

const COMMISSION_DEFAULT = 20

export const ReferralInformation = () => {
  const isMd = useIsMd()
  const { t } = useTranslation()
  const referralWallet = useReferralWallet()
  const { data: referralStatistics, isFetching } = useQueryReferralStatistics(
    {
      walletAddress: referralWallet?.address || '',
    },
    {
      refetchInterval: DURATIONS.ONE_MINUTE_MS,
    }
  )
  const { mutateAsync: generateReferralCode, data: referralCode, isPending } = useMutationGenerateReferralCode()
  const { data: accountCommissionSummary, isLoading } = useQueryAccountCommissionSummary({
    walletAddress: referralWallet?.address || '',
  })

  const { isNftHolders, isKol } = accountCommissionSummary ?? {}
  const isKols = isKol || isNftHolders

  useEffect(() => {
    if (!referralWallet?.address) return
    generateReferralCode({ walletAddress: referralWallet?.address })
  }, [referralWallet?.address])
  const referralLink = `${APP_URL}${PATH_NAME.REFERRAL_HUB}`

  return (
    <Section ring={false} className='space-y-5 flex-1 text-size-14 text-center md:text-left'>
      <div className='flex gap-4 md:gap-8'>
        <ReferralInformationOverview
          title={t('title.REFERRALS')}
          tooltipContent={t('tooltip.REFERRED')}
          isFetching={isFetching}
        >
          {referralStatistics?.data?.totalReferrals || 0} {t('title.REFERRED')}
        </ReferralInformationOverview>
        <ReferralInformationOverview
          title={t('title.YOUR_COMMISSION_RATE')}
          tooltipContent={t('tooltip.EARN_COMMISSION')}
          isFetching={isFetching}
        >
          <TooltipInfo
            side='bottom'
            align={isMd ? 'center' : 'end'}
            alignOffset={isMd ? 0 : -30}
            icon={
              <Text
                size={22}
                color='green'
                className='flex w-fit items-center gap-1 border-b border-dashed border-green-100'
              >
                <CommonGift className='size-6' />
                {isLoading ? '--' : `${formatNumber(accountCommissionSummary?.commission ?? COMMISSION_DEFAULT)}%`}
              </Text>
            }
            contentClassName='py-4 px-6'
            content={
              <div>
                <Text size={22}>{t(isKols ? 'title.PARTNERS_KOL' : 'title.WANT_TO_EARN_MORE')}</Text>
                <Text className='mt-2 mb-4'>
                  <Trans
                    i18nKey={isKols ? 'title.ENJOY_PARTNERS_KOL_FEE_EARN' : 'title.PARTNERS_KOL_FEE_EARN'}
                    values={{
                      feeEarn: t('title.30_COMMISSION'),
                    }}
                    components={[<Text as='span' color='green' />]}
                  />
                </Text>
                <RenderIf condition={!isKols}>
                  <Button onClick={() => window.open(EXTERNAL_LINKS.REGISTER_PARTNER_OR_KOL, '_blank')}>
                    {t('button.BECOME_AN_OPTIMEX_PARTNER_OR_KOL')}
                  </Button>
                </RenderIf>
              </div>
            }
            iconClassName='icon'
          />
        </ReferralInformationOverview>
      </div>
      {/* <div className='flex gap-2 md:gap-10'>
        <ReferralInformationOverview
          title={t('title.REFERRALS')}
          tooltipContent='TODO: Add tooltip info'
          value={referralStatistics?.data?.totalReferrals || 0}
          isFetching={isFetching}
        />
        <Separator orientation='vertical' className='h-16 hidden md:block' />
        <ReferralInformationOverview
          title={t('title.REFERRED_VOLUME')}
          tooltipContent='TODO: Add tooltip info'
          value={formatUSD(referralStatistics?.data?.referredVolumeUsd || 0, {
            showApproximateSign: false,
          })}
          isFetching={isFetching}
        />
        <Separator orientation='vertical' className='h-16 hidden md:block' />
        <ReferralInformationOverview
          title={t('title.OWN_VOLUME')}
          tooltipContent='TODO: Add tooltip info'
          value={formatUSD(referralStatistics?.data?.ownVolumeUsd || 0, {
            showApproximateSign: false,
          })}
          isFetching={isFetching}
        />
      </div> */}
      <Separator />
      <div>
        {t('title.REFERRAL_INFORMATION')}{' '}
        <Text as='a' href={EXTERNAL_LINKS.REFERRAL_PROGRAM_DETAILS} target='_blank' color='main' className='underline'>
          {t('title.LEARN_MORE')}
        </Text>
      </div>
      <div className='flex flex-col md:flex-row gap-3 items-center justify-between border border-dashed border-main-100 rounded-sm py-3 px-5 bg-main-4 my-4'>
        <div>
          <Text size={16} as='span' color='white-48' className='break-all' isLoading={isPending}>
            https://app.optimex.com{PATH_NAME.REFERRAL_HUB}
            {referralCode?.code ? '/' : ''}
          </Text>
          <RenderIf condition={referralCode?.code}>
            <Text size={16} as='span'>
              {referralCode?.code}
            </Text>
          </RenderIf>
        </div>
        <div className='flex gap-3'>
          <CopyToClipboard text={referralCode?.code || ''} copiedSuccessMessage={t('copy.COPY_INVITE_CODE_SUCCESS')}>
            <Button size='sm' className={isPending ? 'pointer-events-none' : ''}>
              <CommonCopy className='size-5' />
              {t('button.COPY_CODE')}
            </Button>
          </CopyToClipboard>
          <CopyToClipboard
            text={`${referralLink}/${referralCode?.code}`}
            copiedSuccessMessage={t('copy.COPY_LINK_SUCCESS')}
          >
            <Button size='sm' className={isPending ? 'pointer-events-none' : ''}>
              <CommonCopy className='size-5' />
              {t('button.COPY_LINK')}
            </Button>
          </CopyToClipboard>
        </div>
      </div>
    </Section>
  )
}
